import { useRef } from 'react'
import {
  Type,
  Barcode,
  QrCode,
  Square,
  Circle,
  Minus,
  Grid2X2,
  Image
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ElementType, ImageElement } from '@/types'

interface ToolItem {
  type: ElementType
  label: string
  icon: React.ReactNode
  category: string
}

const tools: ToolItem[] = [
  // Barcodes (1D)
  { type: 'barcode-code128', label: 'Code 128', icon: <Barcode className="h-5 w-5" />, category: 'Barcodes' },
  { type: 'barcode-code39', label: 'Code 39', icon: <Barcode className="h-5 w-5" />, category: 'Barcodes' },
  { type: 'barcode-ean13', label: 'EAN-13', icon: <Barcode className="h-5 w-5" />, category: 'Barcodes' },
  { type: 'barcode-ean8', label: 'EAN-8', icon: <Barcode className="h-5 w-5" />, category: 'Barcodes' },
  { type: 'barcode-upca', label: 'UPC-A', icon: <Barcode className="h-5 w-5" />, category: 'Barcodes' },
  { type: 'barcode-upce', label: 'UPC-E', icon: <Barcode className="h-5 w-5" />, category: 'Barcodes' },
  { type: 'barcode-itf', label: 'ITF', icon: <Barcode className="h-5 w-5" />, category: 'Barcodes' },
  { type: 'gs1-code128', label: 'GS1-128', icon: <Barcode className="h-5 w-5" />, category: 'Barcodes' },
  // 2D Codes
  { type: 'qrcode', label: 'QR Code', icon: <QrCode className="h-5 w-5" />, category: '2D Codes' },
  { type: 'datamatrix', label: 'DataMatrix', icon: <Grid2X2 className="h-5 w-5" />, category: '2D Codes' },
  { type: 'gs1-datamatrix', label: 'GS1 DataMatrix', icon: <Grid2X2 className="h-5 w-5" />, category: '2D Codes' },
  // Basic
  { type: 'text', label: 'Text', icon: <Type className="h-5 w-5" />, category: 'Basic' },
  // Shapes
  { type: 'line', label: 'Line', icon: <Minus className="h-5 w-5" />, category: 'Shapes' },
  { type: 'rectangle', label: 'Rectangle', icon: <Square className="h-5 w-5" />, category: 'Shapes' },
  { type: 'circle', label: 'Circle', icon: <Circle className="h-5 w-5" />, category: 'Shapes' },
]

// Define category order
const categoryOrder = ['Barcodes', '2D Codes', 'Graphics', 'Basic', 'Shapes']

interface ToolboxPanelProps {
  onAddElement: (type: ElementType) => void
  onAddImage?: (imageElement: Omit<ImageElement, 'id' | 'position' | 'rotation'>) => void
}

export function ToolboxPanel({ onAddElement, onAddImage }: ToolboxPanelProps) {
  // Use predefined category order - include Graphics even if no tools (has Image upload button)
  const categories = categoryOrder.filter(cat => cat === 'Graphics' || tools.some(t => t.category === cat))
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !onAddImage) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const imageData = event.target?.result as string

      // Create a temporary image to get dimensions
      const img = new window.Image()
      img.onload = () => {
        // Calculate default size (max 20mm width, maintain aspect ratio)
        const maxWidthMm = 20
        const aspectRatio = img.width / img.height
        const widthMm = Math.min(maxWidthMm, 20)
        const heightMm = widthMm / aspectRatio

        onAddImage({
          type: 'image',
          width: widthMm,
          height: heightMm,
          imageData,
          originalWidth: img.width,
          originalHeight: img.height,
          name: file.name,
        })
      }
      img.src = imageData
    }
    reader.readAsDataURL(file)

    // Reset input so same file can be selected again
    e.target.value = ''
  }

  return (
    <div className="w-56 border-r bg-card flex flex-col">
      <div className="p-3 border-b">
        <h2 className="font-semibold text-sm">Elements</h2>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/bmp"
            className="hidden"
            onChange={handleImageUpload}
          />

          {categories.map((category, index) => (
            <div key={category}>
              <h3 className="text-xs font-medium text-muted-foreground mb-2">
                {category}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {/* Graphics category has special Image upload button */}
                {category === 'Graphics' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-auto py-3 flex flex-col gap-1"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Image className="h-5 w-5" />
                        <span className="text-xs">Image</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      Upload an image (PNG, JPG)
                    </TooltipContent>
                  </Tooltip>
                )}
                {tools
                  .filter((t) => t.category === category)
                  .map((tool) => (
                    <Tooltip key={tool.type}>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          className="h-auto py-3 flex flex-col gap-1"
                          onClick={() => onAddElement(tool.type)}
                        >
                          {tool.icon}
                          <span className="text-xs">{tool.label}</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        Click to add {tool.label}
                      </TooltipContent>
                    </Tooltip>
                  ))}
              </div>
              {index !== categories.length - 1 && (
                <Separator className="mt-4" />
              )}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}
